import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import I18n from 'app/i18n';
import {faqStore} from 'app/store/global';
import {toastInfo, toastSuccess} from 'app/components/toasts/liteToast';

const VOTE_PHASE = {
    FORM: 'form',
    HIDDEN: 'hidden',
    LOADING: 'loading'
} as const;

type VotePhase = typeof VOTE_PHASE[keyof typeof VOTE_PHASE];

interface Props {
    articleSlug: string
}

const KnowledgeCenterHelpfulVote = observer(({articleSlug}: Props): React.ReactElement | null => {
    const [phase, setPhase] = useState<VotePhase>(VOTE_PHASE.LOADING);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        faqStore.setVoteError(null);
        setPhase(VOTE_PHASE.LOADING);

        let cancelled = false;

        faqStore.fetchHelpfulVoteStatus(articleSlug).then(alreadyVoted => {
            if (cancelled) {
                return;
            }

            setPhase(alreadyVoted ? VOTE_PHASE.HIDDEN : VOTE_PHASE.FORM);
        });

        return () => {
            cancelled = true;
        };
    }, [articleSlug]);

    const finishVote = useCallback((message: string, isDuplicate: boolean): void => {
        setPhase(VOTE_PHASE.HIDDEN);

        if (isDuplicate) {
            toastInfo(message);

            return;
        }

        toastSuccess(message);
    }, []);

    const submitVote = useCallback(async(helpful: boolean) => {
        if (phase !== VOTE_PHASE.FORM || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        const result = await faqStore.submitHelpfulVote(articleSlug, helpful);

        setIsSubmitting(false);

        if (result.success || result.duplicate) {
            finishVote(result.message, result.duplicate);
        }
    }, [
        articleSlug,
        phase,
        isSubmitting,
        finishVote
    ]);

    const handleYesClick = useCallback(() => {
        submitVote(true);
    }, [submitVote]);

    const handleNoClick = useCallback(() => {
        submitVote(false);
    }, [submitVote]);

    if (phase === VOTE_PHASE.HIDDEN || phase === VOTE_PHASE.LOADING) {
        return null;
    }

    return (
        <div className="knowledge-center__vote">
            <span>{I18n.t('knowledge_center.helpful_question')}</span>
            <button
                className="knowledge-center__vote-btn knowledge-center__vote-btn--yes"
                onClick={handleYesClick}
                disabled={isSubmitting}
            >
                {I18n.t('confirm_button_yes')}
            </button>
            <button
                className="knowledge-center__vote-btn knowledge-center__vote-btn--no"
                onClick={handleNoClick}
                disabled={isSubmitting}
            >
                {I18n.t('no')}
            </button>
            {faqStore.voteError && <p className="knowledge-center__vote-error" role="alert">{faqStore.voteError}</p>}
        </div>
    );
});

export default KnowledgeCenterHelpfulVote;
